import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { formatBusinessDate } from '@/lib/format';

export default function InsuranceDiscountsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['insurance-discounts-count'],
        queryFn: async () => {
            const { count, error } = await supabase.from('insurance_premium_discounts').select('*', { count: 'exact', head: true });
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: discounts, isLoading } = useQuery({
        queryKey: ['insurance-discounts', currentPage],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('insurance_premium_discounts')
                .select('*, insurance_premiums(insurances(id, vehicles(vehicle_registrations(number))))')
                .order('created_at', { ascending: false })
                .range(from, to);
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
            </CardHeader>
            <CardContent>
                <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : discounts && discounts.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Insurance</TableHead>
                                <TableHead>Vehicle</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {discounts.map((d) => (
                                <TableRow key={d.id}>
                                    <TableCell>{formatBusinessDate(d.created_at)}</TableCell>
                                    <TableCell>#{d.insurance_premiums?.insurances?.id}</TableCell>
                                    <TableCell>{d.insurance_premiums?.insurances?.vehicles?.vehicle_registrations?.number || '-'}</TableCell>
                                    <TableCell className="text-right font-medium">-</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No insurance discounts found</p>
                )}
            </CardContent>
        </Card>
    );
}
